import React from "react"
import { Box, Typography, Divider as DividerMui } from "@mui/material"
import { styled, ThemeProvider } from "@mui/material/styles"
import receiptTheme from "src/theme/receipt-theme" // Reusing the theme from OrderReceipt
import { Conditional, Stack } from "src/shared/components"
import systemLogo from "src/assets/images/Logo-2.png"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import { useTypedSelector } from "src/shared/store/store"
import moment from "moment"
import { PaymentMethod } from "src/graphql/quark/quark.type"

// Interface for sales category data
export interface XOutPaymentMethodSummary {
  gross: number
  discount: number
  returns: number
  net: number
  sale: number
  tax: number
  transactions: number
  integrationCharges?: number
  platformFee?: number
  shippingFee?: number
}

// Interface for XOutReport data
export interface XOutReportData {
  startDate: string
  endDate: string
  registerName?: string | null
  // storeNumber: number
  [PaymentMethod.Cash]: XOutPaymentMethodSummary
  [PaymentMethod.Card]: XOutPaymentMethodSummary
  [PaymentMethod.Credit]: XOutPaymentMethodSummary
  [PaymentMethod.BankTransfer]: XOutPaymentMethodSummary
  total: XOutPaymentMethodSummary
  printDate: string
}

interface XOutReportReceiptProps {
  reportData: XOutReportData
  receiptRef?: React.RefObject<any>
}

// Main XOutReportReceipt Component
const XOutReportReceipt: React.FC<XOutReportReceiptProps> = ({ reportData, receiptRef }) => {
  const auth = useTypedSelector((state) => state?.authentication)
  const currency = auth?.tenant?.currency

  return (
    <ThemeProvider theme={receiptTheme}>
      <ReceiptContainer ref={receiptRef}>
        {/* Header Section */}
        <ReceiptHeader>
          <img src={auth?.tenant?.invertedLogo as string} width="100px" alt="Logo" />
          <Divider />
          <ReceiptTitle variant="subtitle1">{reportData?.registerName ? "X-OUT REPORT" : "Z Report"}</ReceiptTitle>{" "}
          {/* Tech debt hammad salman*/}
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
            {auth?.currentWhLoc?.name}
          </Typography>
          {/* <Typography variant="subtitle1" sx={{ mt: 0.5 }}>
            {reportData?.storeName}
          </Typography> */}
          <Stack mt={1}>
            {reportData?.registerName ? (
              <Typography textAlign={"left"} variant="subtitle1">
                <strong>Register:</strong> {reportData?.registerName}
              </Typography>
            ) : (
              <></>
            )}
            <Typography textAlign={"left"} variant="subtitle1">
              <strong>Start:</strong> {reportData?.startDate ? moment(reportData?.startDate).format("lll") : ""}
            </Typography>
            <Typography textAlign={"left"} variant="subtitle1">
              <strong>End:</strong> {reportData?.endDate ? moment(reportData?.endDate).format("lll") : ""}
            </Typography>
          </Stack>
        </ReceiptHeader>

        {/* Sales Summary Section */}
        <Divider />
        <SectionTitle variant="subtitle1">SALES SUMMARY</SectionTitle>
        <Divider />

        {/* Cash Sales */}
        <SalesCategory currency={currency} title="Cash Sales" data={reportData?.[PaymentMethod.Cash]} />

        {/* Card Sales */}
        <SalesCategory currency={currency} title="Card Sales" data={reportData?.[PaymentMethod.Card]} />

        {/* Charge Sales */}
        <SalesCategory currency={currency} title="Credit Sales" data={reportData?.[PaymentMethod.Credit]} />

        {/* Cash on Delivery */}
        <Box sx={{ borderBottom: "1px solid black" }}>
          <SalesCategory
            currency={currency}
            title="Bank Transfer Sales"
            data={reportData?.[PaymentMethod.BankTransfer]}
            hasBorder={false}
          />
        </Box>

        {/* Totals */}
        <Box sx={{ pt: 1, pb: 1 }}>
          <CategoryTitle variant="subtitle1">TOTAL</CategoryTitle>
          <SalesRow>
            <Typography variant="subtitle1">Gross:</Typography>
            <Typography variant="subtitle1">{getDisplayPrice(reportData?.total.gross ?? 0)}</Typography>
          </SalesRow>
          <SalesRow>
            <Typography variant="subtitle1">Discount:</Typography>
            <Typography variant="subtitle1">{getDisplayPrice(reportData?.total.discount ?? 0)}</Typography>
          </SalesRow>
          <SalesRow>
            <Typography variant="subtitle1">Tax:</Typography>
            <Typography variant="subtitle1">{getDisplayPrice(reportData?.total.tax ?? 0)}</Typography>
          </SalesRow>
          <SalesRow>
            <Typography variant="subtitle1">Return:</Typography>
            <Typography variant="subtitle1">{getDisplayPrice(reportData?.total.returns ?? 0)}</Typography>
          </SalesRow>
          <Conditional condition={!!reportData?.total.integrationCharges && reportData?.total.integrationCharges > 0}>
            <SalesRow>
              <Typography variant="subtitle1">Integration Charges:</Typography>
              <Typography variant="subtitle1">{getDisplayPrice(reportData?.total.integrationCharges ?? 0)}</Typography>
            </SalesRow>
          </Conditional>
          <Conditional condition={!!reportData?.total.platformFee && reportData?.total.platformFee > 0}>
            <SalesRow>
              <Typography variant="subtitle1">Platform Fee:</Typography>
              <Typography variant="subtitle1">{getDisplayPrice(reportData?.total.platformFee ?? 0)}</Typography>
            </SalesRow>
          </Conditional>
          <Conditional condition={!!reportData?.total.shippingFee && reportData?.total.shippingFee > 0}>
            <SalesRow>
              <Typography variant="subtitle1">Shipping Fee:</Typography>
              <Typography variant="subtitle1">{getDisplayPrice(reportData?.total.shippingFee ?? 0)}</Typography>
            </SalesRow>
          </Conditional>
          <SalesRow>
            <Typography variant="subtitle1" fontWeight={700}>
              Net:
            </Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {getDisplayPrice(reportData?.total.net ?? 0, `${currency ?? ""} `)}
            </Typography>
          </SalesRow>
          <SalesRow>
            <Typography variant="subtitle1" fontWeight={700}>
              Invoices:
            </Typography>
            <Typography variant="subtitle1" fontWeight={700}>
              {getDisplayPrice(reportData?.total.transactions ?? 0)}
            </Typography>
          </SalesRow>
        </Box>

        {/* Rider Info */}
        <Box sx={{ mt: 2, pb: 1, borderTop: "1px solid black" }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ pt: 1 }}>
            Cash Handed Over:
          </Typography>
          <SignatureField />
          <Divider />
        </Box>

        {/* Signatures */}
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Branch Staff Signature:
          </Typography>
          <SignatureField />
          <Divider />

          <Typography variant="subtitle1" fontWeight={700}>
            Receiver Signature:
          </Typography>
          <SignatureField sx={{ mb: 0 }} />
          <Divider />
        </Box>

        {/* Footer */}
        <Divider sx={{ mt: 1 }} />
        {/* <Box sx={{ textAlign: "center", mt: 2, pt: 1, borderTop: "1px solid black" }}>
          <Typography variant="subtitle1">Print: {reportData?.printDate}</Typography>
        </Box> */}
        <Stack variant="columnCenter" mt={0.5}>
          <Typography variant="subtitle1" display="block">
            Powered by
          </Typography>
          <img src={systemLogo} width="100px" />
        </Stack>

        <Typography variant="subtitle1" textAlign={"center"} display="block">
          www.three-shelves.com
        </Typography>
      </ReceiptContainer>
    </ThemeProvider>
  )
}

export default XOutReportReceipt

// Sales Category Component
const SalesCategory: React.FC<{
  title: string
  data: XOutPaymentMethodSummary
  hasBorder?: boolean
  currency?: string | null
}> = ({ title, data, hasBorder = true, currency }) => {
  return (
    <Box sx={{ pb: 1, pt: 1, borderBottom: hasBorder ? "1px dashed rgba(0, 0, 0, 0.4)" : "none" }}>
      <CategoryTitle variant="subtitle1">{title}</CategoryTitle>
      <SalesRow>
        <Typography variant="subtitle1">Gross:</Typography>
        <Typography variant="subtitle1">{getDisplayPrice(data?.gross ?? 0)}</Typography>
      </SalesRow>
      <SalesRow>
        <Typography variant="subtitle1">Discount:</Typography>
        <Typography variant="subtitle1">{getDisplayPrice(data?.discount ?? 0)}</Typography>
      </SalesRow>
      <SalesRow>
        <Typography variant="subtitle1">Tax:</Typography>
        <Typography variant="subtitle1">{getDisplayPrice(data?.tax ?? 0)}</Typography>
      </SalesRow>
      <SalesRow>
        <Typography variant="subtitle1">Return:</Typography>
        <Typography variant="subtitle1">{getDisplayPrice(data?.returns ?? 0)}</Typography>
      </SalesRow>
      <SalesRow>
        <Typography variant="subtitle1">Net Total:</Typography>
        <Typography variant="subtitle1">{getDisplayPrice(data?.net ?? 0, `${currency ?? ""} `)}</Typography>
      </SalesRow>
      <SalesRow>
        <Typography variant="subtitle1">Invoices:</Typography>
        <Typography variant="subtitle1">{getDisplayPrice(data?.transactions ?? 0)}</Typography>
      </SalesRow>
      <Conditional condition={!!data?.integrationCharges && data?.integrationCharges > 0}>
        <SalesRow>
          <Typography variant="subtitle1">Integration Charges:</Typography>
          <Typography variant="subtitle1">{getDisplayPrice(data?.integrationCharges ?? 0)}</Typography>
        </SalesRow>
      </Conditional>
      <Conditional condition={!!data?.platformFee && data?.platformFee > 0}>
        <SalesRow>
          <Typography variant="subtitle1">Platform Fee:</Typography>
          <Typography variant="subtitle1">{getDisplayPrice(data?.platformFee ?? 0)}</Typography>
        </SalesRow>
      </Conditional>
      <Conditional condition={!!data?.shippingFee && data?.shippingFee > 0}>
        <SalesRow>
          <Typography variant="subtitle1">Shipping Fee:</Typography>
          <Typography variant="subtitle1">{getDisplayPrice(data?.shippingFee ?? 0)}</Typography>
        </SalesRow>
      </Conditional>
    </Box>
  )
}

// Styled components for the receipt
const ReceiptContainer = styled(Box)(({ theme }) => ({
  width: "56mm", // Narrower than standard receipt
  margin: "0 auto",
  padding: theme.spacing(1),
  backgroundColor: "#fff",
  boxShadow: theme.shadows[1],
  fontFamily: '"Roboto Mono", "Courier New", monospace',
  fontSize: "0.75rem",
  "@media print": {
    width: "100%",
    height: "100%",
    overflow: "visible",
    breakInside: "avoid",
    pageBreakInside: "avoid",
    padding: theme.spacing(1),
    marginTop: 0
  }
}))

const ReceiptHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(1),
  "@media print": {
    breakInside: "avoid",
    pageBreakInside: "avoid"
  }
}))

const ReceiptTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  padding: theme.spacing(0.5, 0)
}))

const Divider = styled(DividerMui)(() => ({
  borderColor: "black"
}))

// const DividerDashed = styled(DividerMui)(() => ({
//   borderStyle: "dashed",
//   borderColor: "rgba(0, 0, 0, 0.4)"
// }))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  textAlign: "center",
  padding: theme.spacing(0.5, 0),
  margin: theme.spacing(1, 0)
}))

const SalesRow = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: "16px"
}))

const CategoryTitle = styled(Typography)(() => ({
  fontWeight: 700
}))

const SignatureField = styled(Box)(({ theme }) => ({
  height: "32px",
  margin: theme.spacing(1, 0, 2, 0)
}))
